import React, { useState, useEffect } from "react";
import {
  Heart,
  ArrowLeft,
  Star,
  Facebook,
  Instagram,
  Linkedin,
} from "lucide-react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axiosInstance from "../api/api";
import { capitalizeFirstLetter } from "../utils/stringUtils";

const MyFavourites = () => {
  const navigate = useNavigate();
  const { customerId, tabType } = useParams();

  const [activeTab, setActiveTab] = useState((tabType && tabType !== "package") ? tabType : "planner");
  const tabs = ["planner", "performer", "crew"];

  const [favourites, setFavourites] = useState({
    planner: [],
    performer: [],
    crew: [],
  });
  const [loading, setLoading] = useState(true);
  const [customerName, setCustomerName] = useState("");
  const [isOwner, setIsOwner] = useState(true);

  useEffect(() => {
    const fetchFavourites = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`/favourites/${customerId}`);
        if (res.data.success) {
          setFavourites(
            res.data.favourites || {
              planner: [],
              performer: [],
              crew: [],
              package: [],
            },
          );
          setCustomerName(res.data.customerName || "Customer");
          setIsOwner(res.data.isOwner);
        }
      } catch (error) {
        console.error("Error fetching favourites", error);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchFavourites();
    }
  }, [customerId]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/list/${customerId}/${tab}`);
  };

  const activeItems = favourites[activeTab] || [];

  return (
    <div
      className="min-h-screen bg-white font-sans text-[#1A1A1A] mx-auto overflow-x-hidden pt-20"
      style={{ maxWidth: "100%" }}
    >
      <main className="px-4 md:px-8 py-6 md:py-10 max-w-7xl mx-auto">
        <h1 className="text-2xl md:text-4xl font-bold mb-2 md:mb-4">
          {isOwner ? "My Favorites" : `${customerName}'s Shortlist`}
        </h1>

        <button
          onClick={() => navigate("/")}
          className="hidden md:flex items-center text-gray-600 hover:text-black mb-10 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home Page
        </button>

        {/* Tabs - Scrollable on mobile if items increase */}
        <div className="flex gap-4 md:gap-10 border-b border-gray-200 mb-6 md:mb-8 overflow-x-auto no-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`pb-3 md:pb-4 text-base md:text-xl font-medium transition-all relative whitespace-nowrap capitalize ${activeTab === tab ? "text-[#FF5722]" : "text-gray-500"
                }`}
            >
              {tab === "package" ? "Packages" : tab}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 md:h-1 bg-[#FF5722] rounded-t-md" />
              )}
            </button>
          ))}
        </div>

        {/* Cards Grid */}
        {loading ? (
          <div className="text-center py-20 text-gray-500">
            Loading favourites...
          </div>
        ) : activeItems.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            No favourite items found in this category.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
            {activeItems.map((favItem) => {
              const item = favItem.itemId;
              if (!item) return null;

              return (
                <div
                  key={item._id}
                  className="group cursor-pointer shadow-xl border-2 border-gray-200 p-2 md:p-3 min-h-[14rem] md:min-h-[22rem] rounded-xl md:rounded-2xl flex flex-col relative"
                  onClick={() => {
                    if (activeTab === "package") {
                      navigate(`/dashboard/customer/packages/${item._id}`);
                    } else {
                      navigate(`/profile/${item._id}`);
                    }
                  }}
                >
                  <div className="relative aspect-square md:aspect-[4/3] rounded-lg md:rounded-[20px] overflow-hidden mb-2 md:mb-4">
                    <img
                      src={
                        item.profilePicture?.url ||
                        item.images?.[0] ||
                        "https://via.placeholder.com/400x300"
                      }
                      alt={item.companyName || item.title || "Favourite"}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col flex-grow justify-between">
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <h3
                          className="text-[14px] md:text-lg font-bold leading-tight"
                          style={{ fontFamily: "CabinetGrotesk-Bold" }}
                        >
                          {capitalizeFirstLetter(item.companyName || item.title || "Professional")}
                        </h3>
                        <span className="flex items-center text-[10px] md:text-xs font-medium text-gray-600 bg-gray-50 px-1 py-0.5 rounded shrink-0">
                          <Star
                            size={12}
                            className="fill-orange-500 text-orange-500 mr-1"
                          />
                          {item.avgRating || 0}
                        </span>
                      </div>
                      <p className="text-gray-400 text-[10px] md:text-xs mt-1 line-clamp-2 mb-2 md:mb-3">
                        {item.about || item.description || "Event Professional"}
                      </p>
                    </div>
                    <hr className="text-gray-200 w-full mb-2" />
                    <div className="flex flex-row gap-1">
                      <button className="flex-1 py-1 md:py-2 border text-orange-500 border-orange-500 rounded-md text-[10px] md:text-sm text-center whitespace-nowrap">
                        View
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/b2b");
                        }}
                        className="flex-1 py-1 md:py-2 bg-[#FF512F] text-white rounded-md text-[10px] md:text-sm text-center whitespace-nowrap"
                      >
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <footer className="bg-white pt-10 pb-6 px-4 md:px-8 border-t border-gray-100 mt-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center md:items-start gap-8">
          <div className="flex gap-4 md:hidden">
            <Facebook className="w-5 h-5 text-gray-600" />
            <Instagram className="w-5 h-5 text-gray-600" />
            <Linkedin className="w-5 h-5 text-gray-600" />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MyFavourites;
